import { useState } from 'react'
import { AlertTriangle, Plus, ShieldCheck } from 'lucide-react'
import {
  EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { Select } from '@/components/ui/select'
import {
  useWpsFieldSources, useWpsLayoutFields, useWpsLayouts, useWpsMutations,
} from '../api'

/**
 * WPS record layouts.
 *
 * The screen's job is to be honest: no layout ships with this product, and one built here has
 * not been checked against any bank's document until a person says it has, in writing, with a
 * note naming what they read. Until then every layout carries the caveat and so does every
 * file generated from it.
 */
export function WpsLayoutsPage() {
  useBreadcrumb()
  const layouts = useWpsLayouts()
  const sources = useWpsFieldSources()
  const { createLayout, addField, verify } = useWpsMutations()

  const [selected, setSelected] = useState<string | null>(null)
  const fields = useWpsLayoutFields(selected)

  const [layoutDraft, setLayoutDraft] = useState<{
    code: string; name: string; bankName: string; recordFormat: string; delimiter: string
    recordKind: string; fileExtension: string
  } | null>(null)
  const [fieldDraft, setFieldDraft] = useState<{
    fieldOrder: string; name: string; sourceKey: string; literalValue: string
    fieldLength: string; padSide: string; padCharacter: string; formatPattern: string
    mandatory: boolean
  } | null>(null)
  const [verifyDraft, setVerifyDraft] = useState<{ id: string; note: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const chosen = layouts.data?.find((layout) => layout.id === selected)

  return (
    <div className="space-y-4">
      <PageHeader
        title="WPS record layouts"
        subtitle="The file format your bank publishes, entered here field by field."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setLayoutDraft({
                code: '', name: '', bankName: '', recordFormat: 'FIXED_WIDTH',
                delimiter: '', recordKind: 'SDR', fileExtension: 'sif',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New layout
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          No layout is supplied with this system and none can be. The Salary Information File
          format is published by each bank to its own corporate customers, so a layout guessed
          here would produce a file the bank rejects — or, worse, accepts and pays from
          incorrectly. Build yours from your bank's document and validate the first file with
          them before it is used to pay anybody.
        </span>
      </p>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card>
        {layouts.isPending && <Skeleton className="m-4 h-24" />}
        {layouts.isError && (
          <ErrorState error={layouts.error} onRetry={() => void layouts.refetch()} />
        )}
        {layouts.isSuccess && layouts.data.length === 0 && (
          <EmptyState
            title="No layout is defined"
            description="Nothing can be paid through WPS until one exists. Each is built from a bank's own specification."
          />
        )}
        {layouts.isSuccess && layouts.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Bank</th>
                  <th className="p-3 text-start font-medium">Format</th>
                  <th className="p-3 text-end font-medium">Fields</th>
                  <th className="p-3 text-end font-medium">Version</th>
                  <th className="p-3 text-start font-medium">Checked</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {layouts.data.map((layout) => (
                  <tr key={layout.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">{layout.code}</td>
                    <td className="p-3">
                      <button
                        type="button"
                        className="underline underline-offset-2"
                        onClick={() => setSelected(layout.id)}
                      >
                        {layout.name}
                      </button>
                      {layout.verificationCaveat && (
                        <p className="mt-1 max-w-md text-xs text-amber-700">
                          {layout.verificationCaveat}
                        </p>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">{layout.bankName ?? '—'}</td>
                    <td className="p-3 text-slate-600">
                      {layout.recordFormat === 'DELIMITED'
                        ? `Delimited by "${layout.delimiter ?? ''}"` : 'Fixed width'}
                    </td>
                    <td className="p-3 text-end">{layout.fieldCount}</td>
                    <td className="p-3 text-end text-slate-600">v{layout.layoutVersion}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={layout.verifiedAgainstBankSpec ? 'VERIFIED' : 'UNVERIFIED'}
                        tone={layout.verifiedAgainstBankSpec ? 'success' : 'warning'}
                        label={layout.verifiedAgainstBankSpec
                          ? 'Checked against the bank' : 'Not checked'}
                      />
                    </td>
                    <td className="p-3 text-end">
                      {!layout.verifiedAgainstBankSpec && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setError(null)
                            setVerifyDraft({ id: layout.id, note: '' })
                          }}
                        >
                          <ShieldCheck className="size-4" aria-hidden="true" />
                          Record a check
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {chosen && (
        <>
          <PageHeader
            title={`Fields of ${chosen.name}`}
            subtitle="In the order the bank's record puts them."
            actions={
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setError(null)
                  setFieldDraft({
                    fieldOrder: String((fields.data?.length ?? 0) + 1), name: '',
                    sourceKey: '', literalValue: '', fieldLength: '', padSide: 'RIGHT',
                    padCharacter: ' ', formatPattern: '', mandatory: true,
                  })
                }}
              >
                <Plus className="size-4" aria-hidden="true" />
                Add field
              </Button>
            }
          />
          <Card>
            {fields.isPending && <Skeleton className="m-4 h-20" />}
            {fields.isSuccess && fields.data.length === 0 && (
              <EmptyState
                title="This layout has no fields"
                description="It would produce an empty file, so it cannot be generated from or verified yet."
              />
            )}
            {fields.isSuccess && fields.data.length > 0 && (
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-end font-medium">#</th>
                      <th className="p-3 text-start font-medium">Name</th>
                      <th className="p-3 text-start font-medium">Value</th>
                      <th className="p-3 text-end font-medium">Length</th>
                      <th className="p-3 text-start font-medium">Padding</th>
                      <th className="p-3 text-start font-medium">Format</th>
                      <th className="p-3 text-start font-medium">Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.data.map((field) => (
                      <tr key={field.id} className="border-b border-slate-100">
                        <td className="p-3 text-end text-slate-600">{field.fieldOrder}</td>
                        <td className="p-3">{field.name}</td>
                        <td className="p-3 font-mono text-xs">
                          {field.sourceKey === 'LITERAL'
                            ? `"${field.literalValue ?? ''}"` : field.sourceKey}
                        </td>
                        <td className="p-3 text-end">{field.fieldLength ?? '—'}</td>
                        <td className="p-3 text-slate-600">
                          {field.fieldLength == null ? '—'
                            : `${field.padSide === 'LEFT' ? 'Left' : 'Right'} with "${field.padCharacter ?? ' '}"`}
                        </td>
                        <td className="p-3 text-slate-600">{field.formatPattern ?? '—'}</td>
                        <td className="p-3 text-slate-600">
                          {field.mandatory ? 'Yes' : 'Optional'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <FormDialog
        open={layoutDraft != null}
        onOpenChange={(open) => { if (!open) setLayoutDraft(null) }}
        size="lg"
        title="New WPS layout"
        description="It starts unverified. Recording a check is a separate, deliberate act."
        submitLabel="Create layout"
        loading={createLayout.isPending}
        error={error}
        onSubmit={() => {
          if (!layoutDraft) return
          void (async () => {
            setError(null)
            try {
              await createLayout.mutateAsync({
                code: layoutDraft.code,
                name: layoutDraft.name,
                bankName: layoutDraft.bankName || null,
                recordFormat: layoutDraft.recordFormat,
                delimiter: layoutDraft.delimiter || null,
                recordKind: layoutDraft.recordKind,
                fileExtension: layoutDraft.fileExtension,
              })
              setLayoutDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That layout could not be saved')
            }
          })()
        }}
      >
        {layoutDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="layout-code">Code</Label>
              <Input
                id="layout-code" value={layoutDraft.code}
                onChange={(event) =>
                  setLayoutDraft({ ...layoutDraft, code: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="layout-name">Name</Label>
              <Input
                id="layout-name" value={layoutDraft.name}
                onChange={(event) => setLayoutDraft({ ...layoutDraft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="layout-bank">Bank</Label>
              <Input
                id="layout-bank" value={layoutDraft.bankName}
                onChange={(event) =>
                  setLayoutDraft({ ...layoutDraft, bankName: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Whose specification this was built from.
              </p>
            </div>
            <div>
              <Label htmlFor="layout-format">Record format</Label>
              <Select
                id="layout-format"
                className="w-full"
                value={layoutDraft.recordFormat}
                onChange={(event) =>
                  setLayoutDraft({ ...layoutDraft, recordFormat: event.target.value })}
              >
                <option value="FIXED_WIDTH">Fixed width</option>
                <option value="DELIMITED">Delimited</option>
              </Select>
            </div>
            {layoutDraft.recordFormat === 'DELIMITED' && (
              <div>
                <Label htmlFor="layout-delimiter">Delimiter</Label>
                <Input
                  id="layout-delimiter" maxLength={4} value={layoutDraft.delimiter}
                  onChange={(event) =>
                    setLayoutDraft({ ...layoutDraft, delimiter: event.target.value })}
                />
              </div>
            )}
            <div>
              <Label htmlFor="layout-kind">Record kind</Label>
              <Input
                id="layout-kind" value={layoutDraft.recordKind}
                onChange={(event) =>
                  setLayoutDraft({ ...layoutDraft, recordKind: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                SDR or EDR, as your bank's document names it.
              </p>
            </div>
            <div>
              <Label htmlFor="layout-extension">File extension</Label>
              <Input
                id="layout-extension" value={layoutDraft.fileExtension}
                onChange={(event) =>
                  setLayoutDraft({ ...layoutDraft, fileExtension: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={fieldDraft != null}
        onOpenChange={(open) => { if (!open) setFieldDraft(null) }}
        size="lg"
        title="Add a field"
        description="Adding or changing a field bumps the layout version and clears any recorded check."
        submitLabel="Add field"
        loading={addField.isPending}
        error={error}
        onSubmit={() => {
          if (!fieldDraft || !selected) return
          void (async () => {
            setError(null)
            try {
              await addField.mutateAsync({
                id: selected,
                body: {
                  fieldOrder: Number(fieldDraft.fieldOrder),
                  name: fieldDraft.name,
                  sourceKey: fieldDraft.sourceKey,
                  literalValue: fieldDraft.literalValue || null,
                  fieldLength: fieldDraft.fieldLength ? Number(fieldDraft.fieldLength) : null,
                  padSide: fieldDraft.padSide || null,
                  padCharacter: fieldDraft.padCharacter || null,
                  formatPattern: fieldDraft.formatPattern || null,
                  mandatory: fieldDraft.mandatory,
                },
              })
              setFieldDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That field could not be added')
            }
          })()
        }}
      >
        {fieldDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="field-order">Position</Label>
                <Input
                  id="field-order" type="number" value={fieldDraft.fieldOrder}
                  onChange={(event) =>
                    setFieldDraft({ ...fieldDraft, fieldOrder: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="field-name">Name</Label>
                <Input
                  id="field-name" value={fieldDraft.name}
                  onChange={(event) => setFieldDraft({ ...fieldDraft, name: event.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="field-source">What goes here</Label>
                <Select
                  id="field-source"
                  className="w-full"
                  value={fieldDraft.sourceKey}
                  onChange={(event) =>
                    setFieldDraft({ ...fieldDraft, sourceKey: event.target.value })}
                >
                  <option value="">Choose a value…</option>
                  {sources.data?.map((source) => (
                    <option key={source.key} value={source.key}>
                      {source.key} — {source.description}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-slate-600">
                  Only values this system can actually supply. A field it cannot fill would be
                  a blank column in a payment file.
                </p>
              </div>
              {fieldDraft.sourceKey === 'LITERAL' && (
                <div className="sm:col-span-2">
                  <Label htmlFor="field-literal">Fixed text</Label>
                  <Input
                    id="field-literal" value={fieldDraft.literalValue}
                    onChange={(event) =>
                      setFieldDraft({ ...fieldDraft, literalValue: event.target.value })}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="field-length">Length</Label>
                <Input
                  id="field-length" type="number" value={fieldDraft.fieldLength}
                  onChange={(event) =>
                    setFieldDraft({ ...fieldDraft, fieldLength: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  A value longer than this is refused, never truncated.
                </p>
              </div>
              <div>
                <Label htmlFor="field-pad-side">Pad on</Label>
                <Select
                  id="field-pad-side"
                  className="w-full"
                  value={fieldDraft.padSide}
                  onChange={(event) =>
                    setFieldDraft({ ...fieldDraft, padSide: event.target.value })}
                >
                  <option value="RIGHT">The right (text)</option>
                  <option value="LEFT">The left (numbers)</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="field-pad-char">Pad with</Label>
                <Input
                  id="field-pad-char" maxLength={1} value={fieldDraft.padCharacter}
                  onChange={(event) =>
                    setFieldDraft({ ...fieldDraft, padCharacter: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="field-format">Format</Label>
                <Input
                  id="field-format" value={fieldDraft.formatPattern}
                  onChange={(event) =>
                    setFieldDraft({ ...fieldDraft, formatPattern: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  A date pattern such as yyyyMMdd, or IMPLIED to drop an amount's decimal point.
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={fieldDraft.mandatory}
                onChange={(event) =>
                  setFieldDraft({ ...fieldDraft, mandatory: event.target.checked })}
              />
              Required — a record with nothing for this field stops the whole file
            </label>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={verifyDraft != null}
        onOpenChange={(open) => { if (!open) setVerifyDraft(null) }}
        title="Record that you checked this layout"
        description="Name the document and its date. A verification nobody can trace is not one."
        submitLabel="Record the check"
        loading={verify.isPending}
        error={error}
        onSubmit={() => {
          if (!verifyDraft) return
          void (async () => {
            setError(null)
            try {
              await verify.mutateAsync({ id: verifyDraft.id, note: verifyDraft.note })
              setVerifyDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That check could not be recorded')
            }
          })()
        }}
      >
        {verifyDraft && (
          <div>
            <Label htmlFor="verify-note">What did you check it against?</Label>
            <Input
              id="verify-note" value={verifyDraft.note}
              onChange={(event) => setVerifyDraft({ ...verifyDraft, note: event.target.value })}
            />
            <p className="mt-1 text-xs text-slate-600">
              For example: "Bank X SIF specification v3.2, published 2025-11-01". Your name and
              the time are recorded with it.
            </p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
